<template>
  <v-data-table
    :headers="headers"
    :items="listingCosts"
    :sort-by="[{ key: '#', order: 'asc' }]"
  >
    <!-- 1) TOP BAR -->
    <template v-slot:top>
      <v-toolbar flat class="bg-white px-4 py-3">
        <v-row align="center" justify="space-between" no-gutters>
          <!-- Search -->
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search"
              density="comfortable"
              variant="outlined"
              hide-details
              append-inner-icon="mdi-magnify"
              label="Search listing costs..."
              @input="getlistingCosts"
            />
          </v-col>

          <!-- New button -->
          <v-col cols="12" sm="3" class="text-right">
            <v-btn
              variant="elevated"
              color="blue"
              size="large"
              prepend-icon="mdi-plus-thick"
              class="rounded-pill px-6 font-semibold text-white"
              @click="dialog = true"
            >
              New Listing Cost
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>
    </template>

    <!-- 2) DATA COLUMNS (unchanged) -->
    <template v-slot:[`item.id`]="{ index }">
      {{ index + 1 }}
    </template>
    <template v-slot:[`item.description`]="{ item }">
      {{ item.description || "N/A" }}
    </template>

    <!-- 3) ROW ACTIONS -->
    <template v-slot:[`item.actions`]="{ item }">
      <v-btn
        icon
        size="small"
        variant="flat"
        color="primary"
        @click="editItem(item)"
      >
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <v-btn
        icon
        size="small"
        variant="flat"
        color="error"
        @click="deleteItem(item)"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </template>

    <!-- 4) NO-DATA RESET -->
    <template #no-data>
      <v-card flat class="text-center pa-4">
        <v-icon size="36" color="grey">mdi-database-remove</v-icon>
        <div class="text-subtitle-2 mt-2">No listing costs found</div>
        <v-btn color="black" class="mt-2" @click="getlistingCosts">
          Reset
        </v-btn>
      </v-card>
    </template>
  </v-data-table>

  <!-- 5) FORM DIALOG -->
  <v-dialog v-model="dialog" max-width="500px">
    <v-card>
      <v-card-title class="text-h6">
        {{ formTitle }}
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="isValid" @submit.prevent="save">
          <v-row>
            <!-- Name -->
            <v-col cols="12">
              <v-text-field
                v-model="editedItem.title"
                label="Name"
                :rules="rules.name"
              />
            </v-col>
            <!-- Amount -->
            <v-col cols="12">
              <v-text-field
                v-model="editedItem.amount"
                label="Amount"
                :rules="rules.amount"
              />
            </v-col>
            <!-- Category -->
            <v-col cols="12">
              <v-autocomplete
                v-model="editedItem.categoryId"
                :items="categories"
                item-text="title"
                item-value="id"
                label="Select Category"
              />
            </v-col>
            <!-- Description -->
            <v-col cols="12">
              <v-textarea
                v-model="editedItem.description"
                label="Description"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey" @click="close"> Cancel </v-btn>
        <v-btn
          variant="elevated"
          color="black"
          class="rounded-pill px-6 font-semibold text-white"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- 6) DELETE CONFIRMATION DIALOG -->
  <v-dialog v-model="dialogDelete" max-width="400px">
    <v-card>
      <v-card-title class="text-h6 text-center">
        Confirm Deletion
      </v-card-title>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey" @click="closeDelete"> Cancel </v-btn>
        <v-btn
          variant="elevated"
          color="error"
          class="rounded-pill px-6 font-semibold text-white"
          @click="deleteItemConfirm"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ListingCostService from "../services/listing.cost.service.js";
import CategoriesService from "../services/category.services.js";
export default {
  data() {
    const defaultItem = {
      id: null,
      title: "",
      amount: "",
      categoryId: null,
      description: "",
    };
    return {
      dialog: false,
      dialogDelete: false,
      isValid: false,
      rules: {
        name: [
          (value) => {
            if (value?.length >= 3) return true;
            return "You must enter a name.";
          },
        ],
        amount: [
          (value) => {
            if (value?.length >= 1) return true;
            return "You must enter a amount.";
          },
        ],
      },
      headers: [
        {
          title: "#",
          value: "id",
          align: "start",
          sortable: false,
          key: "id",
        },
        { title: "Name", value: "title", key: "title" },
        { title: "Amount(USD)", value: "amount", key: "amount" },
        { title: "Category", value: "category.name", key: "category.name" },
        { title: "Description", value: "description", key: "description" },
        { title: "Actions", key: "actions", sortable: false },
      ],

      editedIndex: -1,
      listingCosts: [],
      categories: [],
      defaultItem,
      editedItem: {
        ...defaultItem,
      },
      defaultItem: {
        id: null,
        categoryId: null,
        title: "",
        amount: "",
        description: "",
      },
    };
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Listing Cost" : "Edit Listing Cost";
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  created() {
    this.getCategories();
    this.getlistingCosts();
  },

  methods: {
    async validate() {
      this.isValid = await this.$refs.form.validate();
    },

    handleLogout() {
      this.$store.commit("logout");
      this.$store.dispatch("resetInactivityTimeout");
      this.$store.dispatch("auth/logout");
      this.$store.dispatch("resetInactivityTimeout");
      this.$router.push("/login");
      this.$store.dispatch("alert/success", "Logout Successfully");
    },

    getlistingCosts() {
      return ListingCostService.index().then(
        (response) => {
          if (response.status == 200) {
            this.listingCosts = response.data.data;
            this.loading = false;
          } else {
            this.listingCosts = [];
            this.loading = false;
            console.log(response.data.console.error);
          }
        },
        (error) => {
          this.listingCosts = [];
          if (error.response.status == 401) {
            this.handleLogout();
          }
          console.log(error);
          this.$store.dispatch("alert/error", error.message);
        }
      );
    },

    getCategories() {
      return CategoriesService.index().then(
        (response) => {
          if (response.status == 200) {
            this.categories = response.data.data;
            this.loading = false;
          } else {
            this.categories = [];
            this.loading = false;
            console.log(response.data.console.error);
          }
        },
        (error) => {
          this.categories = [];
          if (error.response.status == 401) {
            this.handleLogout();
          }
          console.log(error);
          this.$store.dispatch("alert/error", error.message);
        }
      );
    },

    editItem(item) {
      this.editedIndex = this.listingCosts.findIndex((i) => i.id === item.id);
      this.editedItem = {
        ...this.defaultItem,
        id: item.id,
        title: item.title,
        amount: item.amount,
        categoryId: item.category?.id || null,
        description: item.description || "",
      };
      this.dialog = true;
    },

    deleteItem(item) {
      this.editedIndex = this.listingCosts.findIndex((i) => i.id === item.id);
      this.editedItem = { ...item };
      this.dialogDelete = true;
    },

    async deleteItemConfirm() {
      this.loading = true;
      try {
        const response = await ListingCostService.delete({
          id: this.editedItem.id,
        });
        this.$store.dispatch("alert/success", response.data.message);
        this.closeDelete();
        await this.getlistingCosts(); // refresh table
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.$store.dispatch("alert/error", msg);
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        if (this.$refs.form?.resetValidation) {
          this.$refs.form.resetValidation();
        }
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
        this.isValid = false;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
      });
    },

    async save() {
      // 1. Validate form
      await this.validate();
      if (!this.isValid) return;

      this.loading = true;
      const payload = { ...this.editedItem };

      try {
        let response;
        if (this.editedIndex > -1) {
          response = await ListingCostService.update(payload);
        } else {
          response = await ListingCostService.store(payload);
        }
        this.$store.dispatch("alert/success", response.data.message);
        this.close();
        await this.getlistingCosts(); // refresh table
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.$store.dispatch("alert/error", msg);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
